'use client';

import type { ChipProps } from '@mui/material';
import {
  differenceInCalendarDays,
  format,
  formatDistanceStrict,
  parseISO,
  startOfDay,
} from 'date-fns';

import { graphql, type DocumentType } from '@/gql';
import { Or2Chip } from '@/components/data-display/Or2Chip';

const OR2_ASSIGNMENT_URGENCY_CHIP_ASSIGNMENT_FRAGMENT = graphql(`
  fragment or2AssignmentUrgencyChipAssignmentFields on Assignment {
    _id
    dueDate
    status
  }
`);

interface AssignmentUrgencyChipOwnerProps {
  assignment: DocumentType<
    typeof OR2_ASSIGNMENT_URGENCY_CHIP_ASSIGNMENT_FRAGMENT
  >;
  relative?: boolean;
}

type Props = AssignmentUrgencyChipOwnerProps & ChipProps;

type AssignmentUrgencyChipColor = NonNullable<ChipProps['color']>;

export interface AssignmentUrgencyChipProps {
  label: string | null;
  color: AssignmentUrgencyChipColor;
}

const getAssignmentUrgencyChipLabel = (
  due: Date,
  today: Date,
  daysUntilDue: number,
  relative: boolean,
) => {
  if (!relative) {
    return format(due, 'd MMM yyyy');
  }

  const label = formatDistanceStrict(due, today, {
    roundingMethod: 'floor',
  });

  if (daysUntilDue < 0) {
    return `${label} overdue`;
  }

  if (daysUntilDue === 0) {
    return 'Due today';
  }

  return `Due in ${label}`;
};

const getAssignmentUrgencyChipColor = (
  daysUntilDue: number,
): AssignmentUrgencyChipColor => {
  if (daysUntilDue < 0) {
    return 'error';
  }

  if (daysUntilDue <= 7) {
    return 'warning';
  }

  return 'default';
};

export function getOr2AssignmentUrgencyChipProps({
  assignment,
  relative = false,
}: AssignmentUrgencyChipOwnerProps): AssignmentUrgencyChipProps {
  const { dueDate, status } = assignment;

  if (status === 'COMPLETE') {
    return {
      label: null,
      color: 'success',
    };
  }

  if (!dueDate) {
    return { label: 'Open ended', color: 'default' };
  }

  const today = startOfDay(new Date());

  const due = startOfDay(parseISO(dueDate));

  const daysUntilDue = differenceInCalendarDays(due, today);

  const label = getAssignmentUrgencyChipLabel(
    due,
    today,
    daysUntilDue,
    relative,
  );

  const color = getAssignmentUrgencyChipColor(daysUntilDue);

  return { label, color };
}

export const Or2AssignmentUrgencyChip = ({
  assignment,
  relative = false,
  ...chipProps
}: Props) => {
  const { label, color } = getOr2AssignmentUrgencyChipProps({
    assignment,
    relative,
  });

  if (!label) {
    return null;
  }

  return (
    <Or2Chip
      size="small"
      variant="filled"
      {...chipProps}
      label={label}
      color={color}
    />
  );
};
